import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { useAuth, API } from "../context/AuthContext";
import { Package, LogOut, Calendar, ArrowRight } from "lucide-react";

export default function AccountPage() {
  const { user, logout, authHeaders } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/orders`, { headers: authHeaders() });
        if (!cancelled) setOrders(res.data);
      } catch (_e) {
        if (!cancelled) setOrders([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [authHeaders]);

  return (
    <main className="min-h-[70vh]">
      <section className="bg-gradient-to-b from-green-50 to-white border-b border-neutral-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <p className="text-sm font-bold tracking-widest text-green-700">MY ACCOUNT</p>
            <h1 className="mt-1 text-3xl md:text-4xl font-extrabold text-neutral-900">
              Hi, {user?.name?.split(" ")[0] || "there"} 👋
            </h1>
            <p className="mt-1 text-sm text-neutral-600">{user?.email}</p>
          </div>
          <Button
            onClick={logout}
            variant="outline"
            className="rounded-full border-neutral-300 self-start md:self-auto"
          >
            <LogOut size={16} className="mr-1" /> Sign out
          </Button>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-neutral-900 flex items-center gap-2">
            <Package size={20} className="text-green-700" /> Your orders
          </h2>
          <Link to="/plans">
            <Button className="bg-green-600 hover:bg-green-700 text-white rounded-full">
              Order a plan <ArrowRight size={16} className="ml-1" />
            </Button>
          </Link>
        </div>

        <div className="mt-6">
          {loading ? (
            <p className="text-neutral-500">Loading your orders…</p>
          ) : orders.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-neutral-300 p-10 text-center">
              <p className="text-neutral-700 font-semibold">No orders yet</p>
              <p className="mt-1 text-sm text-neutral-500">
                Pick a meal plan to get your first fresh delivery this week.
              </p>
              <Link to="/plans">
                <Button className="mt-5 bg-green-600 hover:bg-green-700 text-white rounded-full">
                  Browse plans
                </Button>
              </Link>
            </div>
          ) : (
            <ul className="grid gap-4">
              {orders.map((o) => (
                <li
                  key={o.id}
                  className="rounded-2xl border border-neutral-200 bg-white p-5 flex flex-wrap items-center justify-between gap-4 hover:border-green-600 hover:shadow-sm transition-all"
                >
                  <div>
                    <p className="font-bold text-neutral-900">{o.plan_name}</p>
                    <p className="text-xs text-neutral-500 flex items-center gap-1 mt-1">
                      <Calendar size={12} />
                      {new Date(o.created_at).toLocaleDateString("en-NZ", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-extrabold text-neutral-900">
                      NZ${o.price}
                      <span className="text-xs font-medium text-neutral-500 ml-1">{o.unit}</span>
                    </p>
                    <span className="inline-block mt-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                      {o.status}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </main>
  );
}
